import {
  Component,
  ChangeDetectionStrategy,
  input,
  ElementRef,
  output,
  viewChildren,
  inject,
  computed
} from '@angular/core';
import {
  Domain,
  HttpRoutingLocation,
  HttpRoutingWithLocations,
  SslStatuses,
  DnsCheckStatuses,
  CdnStatuses
} from '@zerops/models/http-routing';
import { SatPopover } from '@zerops/zef/popover';
import { HttpRoutingItemTranslations } from './http-routing-item.translations';
import { DynamicPopAnchorDirective } from '../dynamic-pop-anchor';
import { TriggerDnsCheckEvent, BadgeType, BadgeClickEvent } from './http-routing-item.model';

@Component({
  selector: 'zui-http-routing-item',
  templateUrl: './http-routing-item.component.html',
  styleUrls: [ './http-routing-item.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HttpRoutingItemComponent {
  // # Deps
  #elementRef = inject(ElementRef);

  // # Data
  // -- sync
  sslStatuses = SslStatuses;
  cdnStatuses = CdnStatuses;
  dnsStatuses = DnsCheckStatuses;

  // -- angular inputs
  data = input.required<HttpRoutingWithLocations>();
  stackLocations = input<HttpRoutingLocation[]>();
  otherLocations = input<HttpRoutingLocation[]>();
  showOtherLocations = input<boolean>(false);
  showSettings = input<boolean>(false);
  translations = input.required<HttpRoutingItemTranslations>();
  dnsCheckStatusPopRef = input<SatPopover>();
  triggerDnsCheckActionKey = input<string>();
  hideOutOfSync = input<boolean>(false);
  sharedBadgePopover = input<SatPopover>();

  // -- angular outputs
  badgeClicked = output<BadgeClickEvent>();
  dnsCheckStatusPopEnter = output<{
    anchor: DynamicPopAnchorDirective,
    name: string
  }>();
  settingsClicked = output<string>();
  dnsCheckStatusPopLeave = output<void>();
  triggerDnsCheckClicked = output<TriggerDnsCheckEvent>();

  // -- computed
  activeConfigMap = computed(() => {
    const locations = [ ...(this.stackLocations() || []), ...(this.otherLocations() || []) ];
    return new Map(
      locations.map((loc) => [
        `${loc.path}${loc.port}`,
        this.#checkHasActiveConfig(loc)
      ])
    );
  });

  // # View Queries
  dnsCheckStatusAnchorRefs = viewChildren<DynamicPopAnchorDirective>('dnsCheckStatusAnchorRef');

  // Public API for ElementRef access (if needed by parent components)
  get elementRef(): ElementRef {
    return this.#elementRef;
  }

  // Emit badge click event - parent handles popover management
  openBadge(
    type: BadgeType,
    domain: Domain,
    anchor: DynamicPopAnchorDirective
  ): void {
    this.badgeClicked.emit({
      type,
      domain,
      anchor,
      httpRoutingId: this.data().id
    });
  }

  trackBy(_: number, item: HttpRoutingLocation) {
    return `${item.path}${item.port}`;
  }

  trackByDomain(_: number, item: Domain) {
    return item.domainName;
  }

  // Badge visibility helpers - direct property checks for O(1) performance
  showSslBadge(domain: Domain): boolean {
    return !!(domain.sslStatus && domain.sslStatus !== SslStatuses.Inactive);
  }

  showCdnBadge(domain: Domain): boolean {
    return !!(domain.cdnStatus && domain.cdnStatus !== CdnStatuses.Disabled);
  }

  // Location config helper - O(1) lookup using computed map
  hasActiveConfig(location: HttpRoutingLocation): boolean {
    return this.activeConfigMap().get(`${location.path}${location.port}`) || false;
  }

  // Private helper - checks if location has any active configuration
  #checkHasActiveConfig(location: HttpRoutingLocation): boolean {
    const config = location?.config;
    if (!config) return false;

    return !!(
      config.redirect?.enabled ||
      config.basicAuth?.enabled ||
      config.accessPolicy?.enabled ||
      config.rateLimiting?.enabled ||
      config.content?.enabled
    );
  }
}
